import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";

export const DummyQuestionPopup = ({
  question,
  currentQuestion,
  isRecording = false,
  stream = null,
  audioStream = null,
  startRecording,
  handleSave,
  onClose,
}) => {
  const [micLevel, setMicLevel] = useState(0);
  const [timer, setTimer] = useState(15);
  const [progress, setProgress] = useState(0);
  const [timerFinished, setTimerFinished] = useState(false);

  const intervalRef = useRef(null);
  const micAnimationRef = useRef(null);
  const videoRef = useRef(null);

  // Countdown
  useEffect(() => {
    setTimer(15);
    setProgress(0);
    setTimerFinished(false);

    intervalRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(intervalRef.current);
          setTimerFinished(true);
          return 0;
        }
        return t - 1;
      });
      setProgress((p) => Math.min(100, p + 100 / 15));
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [currentQuestion]);

  // Auto-start recording after countdown
  useEffect(() => {
    if (timerFinished && !isRecording) {
      startRecording?.();
    }
  }, [timerFinished, isRecording, startRecording]);

  // Attach video stream
//   useEffect(() => {
//     const video = videoRef.current;
//     if (video && stream) {
//       video.srcObject = stream;
//       video.play().catch((err) => console.warn("Video play error:", err));
//     }
//     return () => {
//       if (video) video.srcObject = null;
//     };
//   }, [stream]);

  // Fake mic level animation
  useEffect(() => {
    if (!isRecording) {
      setMicLevel(0);
      return;
    }
    const animate = () => {
      setMicLevel(Math.floor(Math.random() * 70 + 20));
      micAnimationRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(micAnimationRef.current);
  }, [isRecording]);

  // Text-to-speech
  useEffect(() => {
    if (!question?.QuestionDescription) return;

    const speak = () => {
      if (!window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(question.QuestionDescription);
      utterance.rate = 0.9;
      utterance.pitch = 1.2;

      const voices = window.speechSynthesis.getVoices();
      const voice =
        voices.find((v) => v.lang === "en-IN") ||
        voices.find((v) => v.lang.startsWith("en"));

      if (voice) utterance.voice = voice;
      window.speechSynthesis.speak(utterance);
    };

    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        speak();
        window.speechSynthesis.onvoiceschanged = null;
      };
    } else {
      setTimeout(speak, 400);
    }

    return () => window.speechSynthesis?.cancel();
  }, [currentQuestion, question?.QuestionDescription]);
  // Add this new useEffect
useEffect(() => {
  const video = videoRef.current;

  return () => {
    // 1. Immediately detach stream from video
    if (video) {
      video.pause();
      video.srcObject = null;
    }

    // 2. Force stop tracks right when popup closes / save happens
    if (stream) {
      console.log("[Popup Cleanup] Forcing stop on popup close");
      stream.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
    }
  };
}, [stream]);   // ← important: depend on stream

  return (
    <Box
      className="modal-content"
      sx={{
        background: "#fff",
        borderRadius: 2,
        padding: 3,
        width: "100%",
        maxWidth: "620px",
        display: "flex",
        flexDirection: "column",
        height: "75%",
        gap: 3,
        boxShadow: 24,
        position: "relative",
      }}
    >
      <Box className="tour-popup-topline" sx={{ width: "100%", mt: 1 }}>
        <Typography  sx={{ fontSize: 14, mb: 0.5 }}>
          Recording starts in: {timer}s
        </Typography>
        <Box sx={{ width: "100%", height: 10, background: "#e2e8f0", borderRadius: 1, overflow: "hidden" }}>
          <Box
            sx={{
              width: `${progress}%`,
              height: "100%",
              background: "linear-gradient(135deg, #3182ce, #63b3ed)",
              transition: "width 1s linear",
            }}
          />
        </Box>
      </Box>

      <Typography className="question-description" sx={{ fontSize: 20, fontWeight: 600 }}>
        {question?.QuestionDescription || "Question text not available"}
      </Typography>

      <Box
     
        sx={{
          position: "relative",
          width: "100%",
          height: 300,
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        {isRecording && timerFinished && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: "rgba(0,0,0,0.6)",
              padding: "4px 10px",
              borderRadius: 2,
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "red",
                animation: "blink 1s infinite",
              }}
            />
            <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>REC</Typography>
          </Box>
        )}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />

        {!stream && (
          <Box
           className="tour-camera-area"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.5)",
              color: "white",
              zIndex: 5,
            }}
          >
            <Typography variant="h6">Waiting for camera access...</Typography>
          </Box>
        )}
      </Box>

      <Box className="tour-audio-bar" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 14 }}>Mic level</Typography>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              width: "100%",
              height: 20,
              background: "#edf2f7",
              borderRadius: 1,
              border: "1px solid #ccc",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${micLevel}%`,
                height: "100%",
                background: "linear-gradient(135deg, #2f855a, #68d391)",
                transition: "width 0.08s ease",
              }}
            />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 14, minWidth: 30 }}>{micLevel}</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
        className="tour-save-btn"
          onClick={handleSave}
          disabled={!timerFinished}
          sx={{
            background: "linear-gradient(135deg, #f79033, #f05a6e)",
            color: "#fff",
            "&:hover": { opacity: 0.95 },
          }}
        >
          {timerFinished ? "Save" : `Wait (${timer}s)`}
        </Button>
      </Box>

      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </Box>
  );
};